import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import html2pdf from 'html2pdf.js'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'

import {
  CrewUnit,
  CrewDocument,
  CrewDocumentResponse,
  Rank,
  CompanyAdmin,
  Company,
  AcademicQualificationResponse,
  WatchkeepingCertificateResponse,
  NextOfKinResponse,
  BankingDetailsResponse,
  CourseCertificateResponse,
  SeaServiceResponse,
  AdditionalDetailsResponse,
  Reference,
  CrewFlagDocument,
  FlagCountry,
  FlagDocType,
} from '../core/_models'

import {
  getCrewUnit,
  getCrewDocument,
  getCrewDocuments,
  getRanks,
  getCompanyAdminList,
  getCompanyList,
  fetchCrewDocumentBlob,
  getAcademicDetails,
  getWatchkeepingCertificates,
  getNextOfKin,
  getBankingDetails,
  getCourseCertificates,
  getSeaServices,
  getAdditionalDetails,
  getFlagDocuments,
  fetchFlagDocumentBlob,
  fetchWatchkeepingFileBlob,
  fetchCourseCertificateFileBlob,
} from '../core/_requests'
import CrewMatrixProfileModal, {isCrewMatrixRankAllowed} from './CrewMatrixProfileModal'

/** ---------- Helpers ---------- */
const formatBoolean = (val?: boolean | null) =>
  typeof val === 'boolean' ? (val ? 'Yes' : 'No') : '-'

const formatDate = (iso?: string | null) => {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  const dd = String(d.getDate()).padStart(2, '0')
  const mmm = d.toLocaleString('en-US', {month: 'short'})
  const yyyy = d.getFullYear()
  return `${dd}-${mmm}-${yyyy}`
}

const formatAmount = (value?: number | null) => {
  if (value === null || value === undefined) return '-'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '-'
}

const normalizeKey = (s: string) => (s || '').replace(/[^a-z0-9]/gi, '').toLowerCase()

type TemplateLabel =
  | 'CV'
  | 'Passport'
  | 'National CDC'
  | 'Contract'
  | 'Medical'
  | 'Biometric SID'
  | 'Schengen Visa'
  | 'U.S. VISA C1/D'
  | 'National Certificate of Competency (COC)'
  | 'GMDSS'
  | 'GMDSS Endorsement'
  | 'Indos No'
  | 'Others'
  | 'Basic/Adv Oil Endorsement'
  | 'Basic/Adv Chem Endorsement'
  | 'Basic/Adv Gas Endorsement'

type DocumentSlot =
  | 'CV'
  | 'PASSPORT'
  | 'NATIONAL_CDC'
  | 'CONTRACT'
  | 'MEDICAL'
  | 'BIOMETRIC_SID'
  | 'SCHENGEN_VISA'
  | 'US_VISA_C1D'
  | 'COC'
  | 'GMDSS'
  | 'GMDSS_ENDORSEMENT'
  | 'INDOS'
  | 'OTHERS'
  | 'OIL_ENDORSEMENT'
  | 'CHEM_ENDORSEMENT'
  | 'GAS_ENDORSEMENT'

const LABEL_TO_SLOT: Record<TemplateLabel, DocumentSlot> = {
  CV: 'CV',
  Passport: 'PASSPORT',
  'National CDC': 'NATIONAL_CDC',
  Contract: 'CONTRACT',
  Medical: 'MEDICAL',
  'Biometric SID': 'BIOMETRIC_SID',
  'Schengen Visa': 'SCHENGEN_VISA',
  'U.S. VISA C1/D': 'US_VISA_C1D',
  'National Certificate of Competency (COC)': 'COC',
  GMDSS: 'GMDSS',
  'GMDSS Endorsement': 'GMDSS_ENDORSEMENT',
  'Indos No': 'INDOS',
  Others: 'OTHERS',
  'Basic/Adv Oil Endorsement': 'OIL_ENDORSEMENT',
  'Basic/Adv Chem Endorsement': 'CHEM_ENDORSEMENT',
  'Basic/Adv Gas Endorsement': 'GAS_ENDORSEMENT',
}

// --- Flag Documents (view-only section) ---
const FLAG_ORDER: FlagCountry[] = [
  'GAMBIA',
  'COMOROS',
  'MOZAMBIQUE',
  'MARSHALL_ISLANDS',
  'COOK_ISLANDS',
]

const FLAG_LABELS: Record<string, string> = {
  GAMBIA: 'GAMBIA',
  COMOROS: 'COMOROS',
  MOZAMBIQUE: 'MOZAMBIQUE',
  MARSHALL_ISLANDS: 'MARSHALL ISLANDS',
  COOK_ISLANDS: 'COOK ISLANDS',
  PANAMA: 'PANAMA',
  LIBERIA: 'LIBERIA',
  SINGAPORE: 'SINGAPORE',
  MALTA: 'MALTA',
  BAHAMAS: 'BAHAMAS',
  CYPRUS: 'CYPRUS',
  ISLE_OF_MAN: 'ISLE OF MAN',
  HONG_KONG: 'HONG KONG',
  BELIZE: 'BELIZE',
  ST_VINCENT: 'ST. VINCENT & THE GRENADINES',
  ANTIGUA_AND_BARBUDA: 'ANTIGUA & BARBUDA',
  VANUATU: 'VANUATU',
  PALAU: 'PALAU',
  BERMUDA: 'BERMUDA',
  CAYMAN_ISLANDS: 'CAYMAN ISLANDS',
  GIBRALTAR: 'GIBRALTAR',
  GABON: 'GABON',
  TOGO: 'TOGO',
  HONDURAS: 'HONDURAS',
  TUVALU: 'TUVALU',
  INDIA: 'INDIA',
  UNITED_KINGDOM: 'UNITED KINGDOM',
  UNITED_STATES: 'UNITED STATES',
}

const FLAG_DOC_TYPES: FlagDocType[] = [
  'COC',
  'GMDSS',
  'SSO',
  'OIL_ENDORSEMENT',
  'CHEM_ENDORSEMENT',
  'STSDSD',
  'CRA',
]

const FLAG_DOC_LABELS: Record<FlagDocType, string> = {
  COC: 'COC',
  GMDSS: 'GMDSS',
  SSO: 'SSO',
  OIL_ENDORSEMENT: 'OIL ENDORSEMENT',
  CHEM_ENDORSEMENT: 'CHEM ENDORSEMENT',
  STSDSD: 'STSDSD',
  CRA: 'CRA',
}

/** ---------- Component ---------- */
interface Props {
  isOpen: boolean
  crewId?: number
  onClose: () => void
}

const ViewCrewModal: React.FC<Props> = ({isOpen, crewId, onClose}) => {
  const {isSuperAdmin, hasPermission, hasAnyPermission} = useAuth()
  const canExportCrew = isSuperAdmin || hasPermission('DOWNLOAD_CREW_DOCUMENT')
  const canViewCrewDocumentFile = isSuperAdmin || hasPermission('READ_CREW_DOCUMENT')


  // loading
  const [loading, setLoading] = useState(false)

  // data
  const [crew, setCrew] = useState<CrewUnit | null>(null)
  const [docForm, setDocForm] = useState<CrewDocumentResponse | null>(null)
  const [docFiles, setDocFiles] = useState<CrewDocument[]>([])

  const [academicRows, setAcademicRows] = useState<AcademicQualificationResponse[]>([])
  const [watchRows, setWatchRows] = useState<WatchkeepingCertificateResponse[]>([])
  const [kinRow, setKinRow] = useState<NextOfKinResponse | null>(null)
  const [bankingRow, setBankingRow] = useState<BankingDetailsResponse | null>(null)

  const [cgaNameMap, setCgaNameMap] = useState<Record<number, string>>({})

  // Keep Watchkeeping list in the same order as the Add form
  const WATCHKEEPING_ORDER = [
    'Deck Watch keeping Regulation II/4',
    'Able Seafarer Deck Regulation II/5 (COP)',
    'Engine Room Watch keeping III/4',
    'Able Seafarer Engine Regulation III/5 (COP)',
  ] as const

  const COURSE_CATEGORIES = [
    {
      label: 'STCW Certificates',
      courses: [
        'Personal Survival & Social Responsibility (PSSR)',
        'Proficiency in Survival Craft & Rescue Boat (PSCRB)',
        'Proficiency in Survival Technique (PST)',
        'Advanced Fire Fighting (AFF)/ Fire Prevention and Fire Fighting (FPFF)',
        'Elementary First Aid (EFA)/ Medical First Aid (MFA)/ Medicare',
        'STSDSD / SSO Course',
        'High Voltage Training',
        'Radar Observer / ARPA',
        'Radar Simulator (RANSCO) / ENS',
        'LCHS',
        'Ship Safety Officer',
      ],
    },
    {
      label: 'Tanker Courses',
      courses: [
        'Oil Tanker Familiarization (OTFC)/Advance Training for Cargo operation(TASCO)',
        'Chemical Tanker Familiarization (CTFC)',
        'Gas Familiarization (GTFC)',
        'Chemical Tanker Safety (CHEMCO)',
        'Gas Tanker Safety (GASCO)',
      ],
    },
    {
      label: 'Navigation & Main Engine Training',
      courses: [
        'Bridge Team Management (BTM)/Bridge Resource Management (BRM)',
        'Engine Room Simulator (ERS)',
        'ECDIS-Generic',
        'ECDIS-Type Specific',
        'Any Value Added Course/Company Specific Course',
        'Yellow Fever',
      ],
    },
    {
      label: 'Revalidation Course',
      courses: [
        'Refresher and Updating Training (Up gradation course) for Deck Officers',
        'Refresher and Updating Training for Engineer Officers',
        'Add. Training if any',
      ],
    },
    {
      label: 'Value Added Course',
      courses: [],
    },
  ] as const

  const REFERRAL_LABELS: Record<string, string> = {
    WORD_OF_MOUTH: 'Word of mouth',
    PRINT_MEDIA: 'Print media (state which)',
    CONTACTED_BY_STAFF: 'Contacted by Staff',
    WEB_SITES: 'Web Sites',
  }

  // helper mapped types to pick only boolean and string keys from AdditionalDetailsResponse
  type BoolKeysOf<T> = {
    [K in keyof T]-?: NonNullable<T[K]> extends boolean ? K : never
  }[keyof T]

  type StringKeysOf<T> = {
    [K in keyof T]-?: NonNullable<T[K]> extends string ? K : never
  }[keyof T]

  type ADBoolKey = Extract<BoolKeysOf<AdditionalDetailsResponse>, string>
  type ADStringKey = Extract<StringKeysOf<AdditionalDetailsResponse>, string>

  // labels for the "Other Questions" section – keys now strongly typed
  const OTHER_QS: Array<{label: string; flag: ADBoolKey; details: ADStringKey}> = [
    {
      label:
        'Have you been involved in any Incidents of Grounding / Fire / Explosion / Collision / Abandon Ship / Rescue / Major Oil pollution / Drug Smuggling / Towed or Towing another vessel?',
      flag: 'incidentInvolvement',
      details: 'incidentDetails',
    },
    {
      label: 'Have you been involved in a court of Enquiry for a Maritime accident?',
      flag: 'courtOfEnquiryInvolvement',
      details: 'courtOfEnquiryDetails',
    },
    {
      label: 'Have you ever been involved in a criminal case?',
      flag: 'criminalCaseInvolved',
      details: 'criminalCaseDetails',
    },
    {
      label: 'Have you present or previous certificate ever been suspended/revoked?',
      flag: 'certificateSuspendedRevoked',
      details: 'certificateSuspendedRevokedDetails',
    },
    {
      label:
        'Do you suffer or have suffered from: Diabetes/High Blood pressure/Hepatitis/Epilepsy/Nervous Disorders/Disturbed Vision or Hearing/Vertigo?',
      flag: 'hasMedicalConditions',
      details: 'medicalConditionsDetails',
    },
    {
      label: 'Are you a habitual user of Drugs / Narcotics / Excessive Alcohol?',
      flag: 'habitualUseDrugsAlcohol',
      details: 'habitualUseDetails',
    },
    {
      label: 'Have you previously worked with multinational workforce? If Yes, What nationalities?',
      flag: 'workedWithMultinational',
      details: 'multinationalNationalities',
    },
  ]

  // lookups
  const [ranks, setRanks] = useState<Rank[]>([])
  const [rankMap, setRankMap] = useState<Record<number, string>>({})
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdminMap, setCompanyAdminMap] = useState<Record<number, string>>({})
  const [companyMap, setCompanyMap] = useState<Record<number, string>>({})

  // preview viewer
  const [viewer, setViewer] = useState<{
    open: boolean
    url: string | null
    title: string
    mime?: string
  }>({
    open: false,
    url: null,
    title: '',
    mime: undefined,
  })
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  const contentRef = useRef<HTMLDivElement>(null)

  const [courseRows, setCourseRows] = useState<CourseCertificateResponse[]>([])
  const [seaRows, setSeaRows] = useState<SeaServiceResponse[]>([])
  const [otherRow, setOtherRow] = useState<AdditionalDetailsResponse | null>(null)
  const [flagDocs, setFlagDocs] = useState<CrewFlagDocument[]>([])
  const [crewMatrixValues, setCrewMatrixValues] = useState<Partial<Record<string, string>>>({})

  // ⬇️ memos for quick lookup / rendering order
  const courseMap = useMemo(() => {
    const m = new Map<string, CourseCertificateResponse>()
    courseRows?.forEach((r) => {
      const name = (r?.certificateName || '').trim()
      if (!name) return

      const category = (r?.category || '').trim()
      if (category) {
        m.set(`${category}__${name}`, r)
      }

      if (!m.has(`__${name}`)) {
        m.set(`__${name}`, r)
      }
    })
    return m
  }, [courseRows])

  const seaSorted = useMemo(
    () => [...(seaRows || [])].sort((a, b) => (a?.serialNo ?? 0) - (b?.serialNo ?? 0)),
    [seaRows]
  )

  const parsedReferences: Reference[] = useMemo(() => {
    if (!otherRow?.references) return []
    try {
      const arr = JSON.parse(otherRow.references as unknown as string)
      return Array.isArray(arr) ? (arr as Reference[]) : []
    } catch {
      return []
    }
  }, [otherRow])

  const crewMatrixStorageKey = useMemo(
    () => (crewId ? `editCrewMatrixDraft_v1_${crewId}` : ''),
    [crewId]
  )

  /** ---------- effects: load on open ---------- */
  useEffect(() => {
    if (!isOpen || !crewId) {
      setCrew(null)
      setDocForm(null)
      setDocFiles([])
      setAcademicRows([])
      setWatchRows([])
      setKinRow(null)
      setBankingRow(null)
      setCourseRows([])
      setSeaRows([])
      setOtherRow(null)
      setFlagDocs([])
      setCrewMatrixValues({})
      return
    }

    setLoading(true)

    Promise.all([
      getCrewUnit(crewId), // 0
      getRanks(), // 1
      getCrewDocument(crewId).catch(() => null), // 2
      getCrewDocuments(crewId).catch(() => []), // 3
      getCompanyAdminList().catch(() => []), // 4
      getCompanyList().catch(() => []), // 5
      getAcademicDetails(crewId).catch(() => []), // 6
      getWatchkeepingCertificates(crewId).catch(() => []), // 7
      getNextOfKin(crewId).catch(() => null), // 8
      getBankingDetails(crewId).catch(() => null), // 9
      getCourseCertificates(crewId).catch(() => []), // 10
      getSeaServices(crewId).catch(() => []), // 11
      getAdditionalDetails(crewId).catch(() => null), // 12
      getFlagDocuments(crewId).catch(() => []), // 13
    ])
      .then(
        ([
          crewRes,
          rankList,
          docRes,
          files,
          caList,
          cList,
          acad,
          watch,
          kin,
          banking,
          courses,
          sea,
          other,
          flags,
        ]) => {
          setCrew(crewRes)
          setDocForm(docRes)
          setDocFiles(files)
          setFlagDocs(flags || [])

          // rank map
          const rm: Record<number, string> = {}
          rankList.forEach((r) => (rm[r.id] = r.rank))
          setRanks(rankList)
          setRankMap(rm)

          // --- NEW: build CGA name map from BOTH sources ---
          const cgaMap: Record<number, string> = {}

          // Source A: /company-group-admins (works in superadmin)
          caList.forEach((cga) => {
            if (cga?.id != null) cgaMap[cga.id] = cga.name || String(cga.id)
          })

          // Source B: companies list with cga embedded (works in company login)
          cList.forEach((co) => {
            const id = co?.cga?.id
            const name = co?.cga?.name
            if (id != null && name) cgaMap[id] = name
          })

          setCgaNameMap(cgaMap)

          // keep your existing company map for “Sub Company (Company Admin)”
          const cm: Record<number, string> = {}
          cList.forEach((co) => (cm[co.id] = co.name || co.username || String(co.id)))
          setCompanyMap(cm)

          // already-present tabs
          setAcademicRows(acad || [])
          setWatchRows(watch || [])
          setKinRow(kin)
          setBankingRow(banking)

          // NEW tabs 6/7/8
          setCourseRows(courses || [])
          setSeaRows(sea || [])
          setOtherRow(other)
        }
      )
      .finally(() => setLoading(false))
  }, [isOpen, crewId])

  useEffect(() => {
    if (!isOpen || !crewMatrixStorageKey) {
      setCrewMatrixValues({})
      return
    }

    try {
      const raw = localStorage.getItem(crewMatrixStorageKey)
      if (!raw) {
        setCrewMatrixValues({})
        return
      }

      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setCrewMatrixValues(parsed as Partial<Record<string, string>>)
      } else {
        setCrewMatrixValues({})
      }
    } catch {
      setCrewMatrixValues({})
    }
  }, [isOpen, crewMatrixStorageKey])

  /** ---------- doc files (by slot) ---------- */
  const cocDocs = useMemo(
    () =>
      (docFiles || [])
        .filter((d) => d?.slot === 'COC' && d?.id)
        .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1)),
    [docFiles]
  )

  const bySlot = useMemo(() => {
    const m = new Map<DocumentSlot, CrewDocument>()
    docFiles.forEach((d) => {
      if (!d?.slot || d.slot === 'COC') return
      m.set(d.slot as DocumentSlot, d)
    })
    return m
  }, [docFiles])

  const hasDoc = (slot: DocumentSlot) => !!bySlot.get(slot)?.id

  const renderViewBtn = (label: TemplateLabel, slot: DocumentSlot) =>
    hasDoc(slot) && canViewCrewDocumentFile ? (
      <button className='btn btn-sm btn-info' onClick={() => openPreview(slot, label)}>
        View
      </button>
    ) : (
      <span>-</span>
    )

  /** ---------- viewer ---------- */
  const closeViewer = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
      setObjectUrl(null)
    }
    setViewer({open: false, url: null, title: '', mime: undefined})
  }

  const openPreview = async (slot: DocumentSlot, title: string) => {
    if (!crewId) return
    const doc = bySlot.get(slot)
    if (!doc?.id) return
    try {
      const blob = await fetchCrewDocumentBlob(crewId, doc.id)
      const url = URL.createObjectURL(blob)
      setObjectUrl(url)
      setViewer({open: true, url, title, mime: blob.type || undefined})
    } catch (e) {
      console.error('Preview failed', e)
    }
  }

  const openPreviewByDoc = async (doc: CrewDocument, title: string) => {
    if (!crewId || !doc?.id) return
    try {
      const blob = await fetchCrewDocumentBlob(crewId, doc.id)
      const url = URL.createObjectURL(blob)
      setObjectUrl(url)
      setViewer({open: true, url, title, mime: blob.type || undefined})
    } catch (e) {
      console.error('Preview failed', e)
    }
  }

  const openFlagPreview = async (doc: CrewFlagDocument, title: string) => {
    if (!crewId || !doc?.id) return
    try {
      const blob = await fetchFlagDocumentBlob(crewId, doc.id)
      const url = URL.createObjectURL(blob)
      setObjectUrl(url)
      setViewer({open: true, url, title, mime: blob.type || undefined})
    } catch (e) {
      console.error('Flag preview failed', e)
    }
  }

  const openWatchPreview = async (row: WatchkeepingCertificateResponse) => {
    if (!crewId || !row?.id) return
    try {
      const blob = await fetchWatchkeepingFileBlob(crewId, row.id)
      const url = URL.createObjectURL(blob)
      setObjectUrl(url)
      setViewer({
        open: true,
        url,
        title: row.certificateDetails || 'Watchkeeping Certificate',
        mime: blob.type || undefined,
      })
    } catch (e) {
      console.error('Watchkeeping preview failed', e)
    }
  }

  const openCoursePreview = async (row: CourseCertificateResponse) => {
    if (!row?.id) return
    try {
      const blob = await fetchCourseCertificateFileBlob(row.id)
      const url = URL.createObjectURL(blob)
      setObjectUrl(url)
      setViewer({
        open: true,
        url,
        title: row.certificateName || 'Course Certificate',
        mime: blob.type || undefined,
      })
    } catch (e) {
      console.error('Course preview failed', e)
    }
  }

  /** ---------- export PDF ---------- */
  const handleDownloadPDF = () => {
    if (!contentRef.current) return
    const options = {
      margin: 0.5,
      filename: `${crew?.name || 'crew-details'}.pdf`,
      image: {type: 'jpeg', quality: 0.98},
      html2canvas: {scale: 2},
      jsPDF: {unit: 'in', format: 'a4', orientation: 'portrait'},
    }
    html2pdf().set(options).from(contentRef.current).save()
  }

  if (!isOpen || !crewId) return null

  const presentRankName = rankMap[(crew?.presentRankId as number) ?? crew?.rankId ?? -1] ?? '-'
  const appliedRankName =
    crew?.rankAppliedForId != null ? rankMap[crew.rankAppliedForId] ?? '-' : '-'
  const fallbackRankName =
    rankMap[crew?.rankId ?? -1] ??
    (crew?.rankAppliedForId != null ? rankMap[crew.rankAppliedForId] ?? '' : '')
  const showCrewMatrixForRank = isCrewMatrixRankAllowed(
    presentRankName !== '-' ? presentRankName : fallbackRankName
  )

  const matrixCrewRecord = crew
    ? {
        id: crew.id ?? crewId,
        crewId: crew.id ?? crewId,
        name: crew.name || '',
        crewName: crew.name || '',
        nationality: crew.nationality || '',
        rankId: (crew?.presentRankId as number) ?? crew?.rankId ?? undefined,
        rankInfo:
          presentRankName !== '-'
            ? {
                id: (crew?.presentRankId as number) ?? crew?.rankId ?? 0,
                rank: presentRankName,
              }
            : undefined,
        rank: presentRankName !== '-' ? presentRankName : '',
        dateOfAvailability: crew.dateOfAvailability || otherRow?.availabilityDate || '',
        dateOfJoining: crew.dateOfJoining || crew.date_of_joining || '',
        signOnDate: (otherRow as any)?.signOnDate || '',
        ...(docForm ?? {}),
        crewDocuments: {
          ...(docForm ?? {}),
        },
        additionalInfo: {
          ...(otherRow ?? {}),
        },
        academicQualifications: Array.isArray(academicRows) ? academicRows : [],
        courses: Array.isArray(courseRows) ? courseRows : [],
        seaServiceHistory: Array.isArray(seaRows) ? seaRows : [],
        documents: Array.isArray(docFiles) ? docFiles : [],
        activeSignOn: (crew as any)?.activeSignOn,
      }
    : null

  return (
    <>
      <div className='modal-overlay' onClick={onClose}>
        <div
          className='modal-content'
          onClick={(e) => e.stopPropagation()}
          style={{maxWidth: '90rem'}}
        >
          <div className='custom-modal-header d-flex justify-content-between align-items-center'>
            <h5 className='m-0'>View Crew Details</h5>
            <div className='d-flex gap-2 align-items-center'>
              {canExportCrew && (
              <button className='btn btn-sm btn-light' onClick={handleDownloadPDF}>
                Download PDF
              </button>
              )}
              <button className='close-btn' onClick={onClose}>
                <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
              </button>
            </div>
          </div>

          <Modal.Body style={{maxHeight: '75vh', overflowY: 'auto'}}>
            <div ref={contentRef} className='custom-modal-body'>
              {/* Loading state */}
              {loading && (
                <div className='d-flex align-items-center gap-3 alert alert-info'>
                  <div className='spinner-border spinner-border-sm' role='status' />
                  <span>Loading crew details…</span>
                </div>
              )}

              {!loading && !crew && (
                <div className='alert alert-danger'>Failed to load crew details.</div>
              )}

              {/* ------------------ PERSONAL (tabs: Personal Data) ------------------ */}
              {crew && (
                <>
                  <h5 className='mb-3'>Personal Data</h5>
                  <div className='row'>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Present Rank</label>
                      <p>{presentRankName}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Rank Applied For</label>
                      <p>{appliedRankName}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Crew Id / Employee Id</label>
                      <p>{(crew as any).employeeId || 'N/A'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Family Name</label>
                      <p>{crew.familyName ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Tel No</label>
                      <p>{crew.telNo ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Full Name</label>
                      <p>{crew.name ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Mobile No</label>
                      <p>{crew.contactNumber ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Email Id</label>
                      <p>{crew.email ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Alternate Mobile No</label>
                      <p>{crew.alternateMobNo ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Date of Birth</label>
                      <p>{formatDate(crew.dateOfBirth)}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Skype Id</label>
                      <p>{crew.skypeId ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Nationality</label>
                      <p>{crew.nationality ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Place of Birth</label>
                      <p>{crew.placeOfBirth ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Date of Availability</label>
                      <p>{formatDate(crew.dateOfAvailability)}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Marital Status</label>
                      <p>{crew.maritalStatus ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Department</label>
                      <p>{crew.department ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Willing to Accept Lower Rank</label>
                      <p>{formatBoolean(crew.willingToAcceptLowerRank)}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Boiler Suit Size</label>
                      <p>{crew.boilerSuitSize ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Height (cm)</label>
                      <p>{crew.heightCms ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Weight (kg)</label>
                      <p>{crew.weightKgs ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>BMI Index</label>
                      <p>{crew.bmiIndex ?? '-'}</p>
                    </div>

                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Blood Group</label>
                      <p>{crew.bloodGroup ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Shoe Size</label>
                      <p>{crew.shoeSize ?? '-'}</p>
                    </div>

                    <div className='col-12 mb-3'>
                      <label className='modal_label'>Address</label>
                      <p style={{whiteSpace: 'pre-wrap'}}>{crew.address ?? '-'}</p>
                    </div>

                    {/* Company mappings (best effort) */}
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Company</label>
                      <p>
                        {crew.companyGroupAdminId != null
                          ? cgaNameMap[crew.companyGroupAdminId] ??
                            `ID: ${crew.companyGroupAdminId}`
                          : '-'}
                      </p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Sub Company</label>
                      <p>
                        {crew.companyAdminId != null
                          ? companyMap[crew.companyAdminId] ?? `ID: ${crew.companyAdminId}`
                          : '-'}
                      </p>
                    </div>

                    {/* National CDC Details */}
                    <div className='col-12 mt-3 mb-2'>
                      <h6 style={{color: '#1e40af', fontWeight: 600, borderBottom: '1px solid #e5e7eb', paddingBottom: 4}}>
                        National CDC Details
                      </h6>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>CDC Number</label>
                      <p>{docForm?.cdcNumber ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>CDC Place of Issue</label>
                      <p>{docForm?.cdcPlaceOfIssue ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>CDC Date of Issue</label>
                      <p>{formatDate(docForm?.cdcIssueDate)}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>CDC Date of Expiry</label>
                      <p>{formatDate(docForm?.cdcExpiryDate)}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>Union Membership No</label>
                      <p>{docForm?.unionMembershipNo ?? '-'}</p>
                    </div>
                    <div className='col-md-6 mb-3'>
                      <label className='modal_label'>National CDC Document</label>
                      <div>{renderViewBtn('National CDC', 'NATIONAL_CDC')}</div>
                    </div>

                    {/* Optional notes */}
                    {crew.notes && (
                      <div className='col-12 mb-2'>
                        <label className='modal_label'>Notes</label>
                        <p style={{whiteSpace: 'pre-wrap'}}>{crew.notes}</p>
                      </div>
                    )}
                  </div>

                  <hr className='my-5' />

                  {/* ------------------ DOCUMENTS (tabs: Documents) ------------------ */}
                  <h5 className='mb-3'>Identity Documents</h5>
                  <div className='table-responsive'>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th>Documents</th>
                          <th>Number</th>
                          <th>Date of Issue</th>
                          <th>Place of Issue</th>
                          <th>Date of Expiry</th>
                          <th>ECNR</th>
                          <th>Minimum 4 Blank Pages</th>
                          <th>Union Membership No</th>
                          <th>File</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <strong>CV</strong>
                          </td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{renderViewBtn('CV', 'CV')}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Passport</strong>
                          </td>
                          <td>{docForm?.passportNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.passportIssueDate)}</td>
                          <td>{docForm?.passportPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.passportExpiryDate)}</td>
                          <td>{formatBoolean(docForm?.passportEcnr)}</td>
                          <td>{formatBoolean(docForm?.passportMin4BlankPages)}</td>
                          <td>-</td>
                          <td>{renderViewBtn('Passport', 'PASSPORT')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Contract</strong>
                          </td>
                          <td>{docForm?.contractNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.contractIssueDate)}</td>
                          <td>{docForm?.contractPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.contractExpiryDate)}</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{renderViewBtn('Contract', 'CONTRACT')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Medical</strong>
                          </td>
                          <td>{docForm?.medicalNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.medicalIssueDate)}</td>
                          <td>{docForm?.medicalPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.medicalExpiryDate)}</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{renderViewBtn('Medical', 'MEDICAL')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Biometric SID</strong>
                          </td>
                          <td>{docForm?.sidNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.sidIssueDate)}</td>
                          <td>{docForm?.sidPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.sidExpiryDate)}</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{renderViewBtn('Biometric SID', 'BIOMETRIC_SID')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Schengen Visa</strong>
                          </td>
                          <td>{docForm?.schengenVisaNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.schengenVisaIssueDate)}</td>
                          <td>{docForm?.schengenVisaPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.schengenVisaExpiryDate)}</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{renderViewBtn('Schengen Visa', 'SCHENGEN_VISA')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>U.S. VISA C1/D</strong>
                          </td>
                          <td>{docForm?.usVisaC1dNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.usVisaC1dIssueDate)}</td>
                          <td>{docForm?.usVisaC1dPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.usVisaC1dExpiryDate)}</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{renderViewBtn('U.S. VISA C1/D', 'US_VISA_C1D')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h5 className='mt-5 mb-3'>Professional Certificates</h5>
                  <div className='table-responsive'>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th>Documents</th>
                          <th>Grade/Level</th>
                          <th>Number</th>
                          <th>Issuing Authority</th>
                          <th>Date of Issue</th>
                          <th>Date of Expiry</th>
                          <th>File</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <strong>National Certificate of Competency (COC)</strong>
                          </td>
                          <td>
                            <div>{docForm?.cocGradeLevel ?? '-'}</div>
                            <small className='text-muted d-block mt-1'>
                              COC Limitations:{' '}
                              {docForm?.cocHasLimitations === true
                                ? 'Yes'
                                : docForm?.cocHasLimitations === false
                                ? 'No'
                                : '-'}
                            </small>
                            {docForm?.cocHasLimitations === true && (
                              <small className='text-muted d-block'>
                                {docForm?.cocLimitations || '-'}
                              </small>
                            )}
                          </td>
                          <td>{docForm?.cocNumber ?? '-'}</td>
                          <td>
                            <div>{docForm?.cocIssuingAuthority ?? '-'}</div>
                            <small className='text-muted d-block mt-1'>
                              Admin Acceptance:{' '}
                              {docForm?.cocAdminAcceptance === true
                                ? 'Yes'
                                : docForm?.cocAdminAcceptance === false
                                ? 'No'
                                : '-'}
                            </small>
                          </td>
                          <td>{formatDate(docForm?.cocIssueDate)}</td>
                          <td>{formatDate(docForm?.cocExpiryDate)}</td>
                          <td>
                            {cocDocs.length === 0 ? (
                              <span>-</span>
                            ) : (
                              <div className='d-flex flex-column gap-2'>
                                {cocDocs.map((doc, idx) => {
                                  const fileName =
                                    (doc.filePath || '').split('/').pop() || `COC File ${idx + 1}`
                                  return (
                                    <div key={doc.id} className='d-flex align-items-center gap-2'>
                                      <span
                                        className='small text-truncate'
                                        style={{maxWidth: '180px'}}
                                        title={fileName}
                                      >
                                        {fileName}
                                      </span>
                                      {canViewCrewDocumentFile && (
                                        <button
                                          className='btn btn-sm btn-info'
                                          onClick={() =>
                                            openPreviewByDoc(
                                              doc,
                                              `National Certificate of Competency (COC) - ${idx + 1}`
                                            )
                                          }
                                        >
                                          View
                                        </button>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <strong>GMDSS</strong>
                          </td>
                          <td>{docForm?.gmdssGradeLevel ?? '-'}</td>
                          <td>{docForm?.gmdssNumber ?? '-'}</td>
                          <td>{docForm?.gmdssIssuingAuthority ?? '-'}</td>
                          <td>{formatDate(docForm?.gmdssIssueDate)}</td>
                          <td>{formatDate(docForm?.gmdssExpiryDate)}</td>
                          <td>{renderViewBtn('GMDSS', 'GMDSS')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>GMDSS Endorsement</strong>
                          </td>
                          <td>{docForm?.gmdssEndorsementGradeLevel ?? '-'}</td>
                          <td>{docForm?.gmdssEndorsementNumber ?? '-'}</td>
                          <td>{docForm?.gmdssEndorsementIssuingAuthority ?? '-'}</td>
                          <td>{formatDate(docForm?.gmdssEndorsementIssueDate)}</td>
                          <td>{formatDate(docForm?.gmdssEndorsementExpiryDate)}</td>
                          <td>{renderViewBtn('GMDSS Endorsement', 'GMDSS_ENDORSEMENT')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Indos No</strong>
                          </td>
                          <td>{docForm?.indosGradeLevel ?? '-'}</td>
                          <td>{docForm?.indosNumber ?? '-'}</td>
                          <td>{docForm?.indosIssuingAuthority ?? '-'}</td>
                          <td>{formatDate(docForm?.indosIssueDate)}</td>
                          <td>{formatDate(docForm?.indosExpiryDate)}</td>
                          <td>{renderViewBtn('Indos No', 'INDOS')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Others</strong>
                          </td>
                          <td>{docForm?.otherDocGradeLevel ?? '-'}</td>
                          <td>{docForm?.otherDocNumber ?? '-'}</td>
                          <td>{docForm?.otherDocIssuingAuthority ?? '-'}</td>
                          <td>{formatDate(docForm?.otherDocIssueDate)}</td>
                          <td>{formatDate(docForm?.otherDocExpiryDate)}</td>
                          <td>{renderViewBtn('Others', 'OTHERS')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h5 className='mt-5 mb-3'>Dangerous Cargo Endorsements</h5>
                  <div className='table-responsive'>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th>Dangerous Cargo Endorsements</th>
                          <th>Nationality</th>
                          <th>Grade/Level I/II</th>
                          <th>Number</th>
                          <th>Date of Issue</th>
                          <th>Place of Issue</th>
                          <th>Date of Expiry</th>
                          <th>File</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Basic/Adv Oil Endorsement</strong>
                          </td>
                          <td>{docForm?.oilEndorsementNationality ?? '-'}</td>
                          <td>{docForm?.oilEndorsementGradeLevel ?? '-'}</td>
                          <td>{docForm?.oilEndorsementNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.oilEndorsementIssueDate)}</td>
                          <td>{docForm?.oilEndorsementPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.oilEndorsementExpiryDate)}</td>
                          <td>{renderViewBtn('Basic/Adv Oil Endorsement', 'OIL_ENDORSEMENT')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Basic/Adv Chem Endorsement</strong>
                          </td>
                          <td>{docForm?.chemEndorsementNationality ?? '-'}</td>
                          <td>{docForm?.chemEndorsementGradeLevel ?? '-'}</td>
                          <td>{docForm?.chemEndorsementNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.chemEndorsementIssueDate)}</td>
                          <td>{docForm?.chemEndorsementPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.chemEndorsementExpiryDate)}</td>
                          <td>{renderViewBtn('Basic/Adv Chem Endorsement', 'CHEM_ENDORSEMENT')}</td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Basic/Adv Gas Endorsement</strong>
                          </td>
                          <td>{docForm?.gasEndorsementNationality ?? '-'}</td>
                          <td>{docForm?.gasEndorsementGradeLevel ?? '-'}</td>
                          <td>{docForm?.gasEndorsementNumber ?? '-'}</td>
                          <td>{formatDate(docForm?.gasEndorsementIssueDate)}</td>
                          <td>{docForm?.gasEndorsementPlaceOfIssue ?? '-'}</td>
                          <td>{formatDate(docForm?.gasEndorsementExpiryDate)}</td>
                          <td>{renderViewBtn('Basic/Adv Gas Endorsement', 'GAS_ENDORSEMENT')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* ------------------------------------------------------------------ */}
                  {/* TAB 3 — Academic Background (view only)                            */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>Academic Background</h5>
                  <div className='table-responsive'>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th>Qualification</th>
                          <th>Name of Institution</th>
                          <th>Board/University</th>
                          <th>Date of Passing</th>
                          <th>Grade/Percentage</th>
                          <th>English Proficiency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {academicRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className='text-center text-muted'>
                              No academic records.
                            </td>
                          </tr>
                        ) : (
                          academicRows.map((row, idx) => (
                            <tr key={idx}>
                              <td>{row.qualification || '-'}</td>
                              <td>{row.institutionName || '-'}</td>
                              <td>{row.boardOrUniversity || '-'}</td>
                              <td>{formatDate(row.dateOfPassing)}</td>
                              <td>{row.gradeOrPercentage || '-'}</td>
                              <td>{row.englishProficiency || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ------------------------------------------------------------------ */}
                  {/* TAB 4 — Watch keeping certificates (For Rating only)               */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>
                    Watch keeping certificates{' '}
                    <span className='fw-normal'>
                      (For <strong>Rating</strong> only)
                    </span>
                  </h5>
                  <div className='table-responsive'>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th>Certificate Details</th>
                          <th>Certificate No</th>
                          <th>Date of Issue</th>
                          <th>Place of Issue</th>
                          <th>Valid Until</th>
                          <th>File</th>
                        </tr>
                      </thead>
                      <tbody>
                        {WATCHKEEPING_ORDER.map((label, idx) => {
                          const row = watchRows.find((w) => w.certificateDetails === label)
                          return (
                            <tr key={idx}>
                              <td>{label}</td>
                              <td>{row?.certificateNo || '-'}</td>
                              <td>{formatDate(row?.dateOfIssue)}</td>
                              <td>{row?.placeOfIssue || '-'}</td>
                              <td>{formatDate(row?.validUntil)}</td>
                              <td>
                                {row?.filePath && canViewCrewDocumentFile ? (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-info'
                                    onClick={() => openWatchPreview(row)}
                                  >
                                    View
                                  </button>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                        {watchRows.length === 0 && (
                          <tr>
                            <td colSpan={6} className='text-center text-muted'>
                              No watchkeeping certificates.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ------------------------------------------------------------------ */}
                  {/* TAB 5 — Next of Kin Details (view only)                            */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>Next of Kin Details</h5>
                  {!kinRow ? (
                    <div className='text-muted'>No next of kin details.</div>
                  ) : (
                    <div className='row'>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Civil Status</label>
                        <p>{kinRow.civilStatus ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Full Name of Next of Kin</label>
                        <p>{kinRow.fullName ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Relationship</label>
                        <p>{kinRow.relationship ?? '-'}</p>
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='modal_label'>Address of Next of Kin</label>
                        <p style={{whiteSpace: 'pre-wrap'}}>{kinRow.address ?? '-'}</p>
                      </div>
                      <div className='col-md-4 mb-3'>
                        <label className='modal_label'>Pin Code</label>
                        <p>{kinRow.pinCode ?? '-'}</p>
                      </div>
                      <div className='col-md-4 mb-3'>
                        <label className='modal_label'>Phone STD Code</label>
                        <p>{kinRow.phoneStdCode ?? '-'}</p>
                      </div>
                      <div className='col-md-4 mb-3'>
                        <label className='modal_label'>Phone Number</label>
                        <p>{kinRow.phoneNumber ?? '-'}</p>
                      </div>
                    </div>
                  )}
                  {/* ------------------------------------------------------------------ */}
                  {/* TAB 6 — Banking Details                                            */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>Banking Details</h5>
                  {!bankingRow ? (
                    <div className='text-muted'>No banking details.</div>
                  ) : (
                    <div className='row'>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Name of Beneficiary</label>
                        <p>{bankingRow.nameOfBeneficiary ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Account Number</label>
                        <p>{bankingRow.accountNumber ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Bank Name</label>
                        <p>{bankingRow.bankName ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Branch Name</label>
                        <p>{bankingRow.branchName ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Branch Code</label>
                        <p>{bankingRow.branchCode ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Bank Code</label>
                        <p>{bankingRow.ifscRtgsType === 'RTGS' ? 'IFSC / RTGS' : 'SWIFT / IBAN'}</p>
                      </div>
                      {bankingRow.ifscRtgsType === 'RTGS' ? (
                        <div className='col-md-6 mb-3'>
                          <label className='modal_label'>IFSC / RTGS Code</label>
                          <p>{bankingRow.ifscRtgsCode ?? '-'}</p>
                        </div>
                      ) : (
                        <div className='col-md-6 mb-3'>
                          <label className='modal_label'>SWIFT / IBAN Code</label>
                          <p>{bankingRow.swiftIandi ?? '-'}</p>
                        </div>
                      )}
                      <div className='col-12 mb-3'>
                        <label className='modal_label'>Bank Address</label>
                        <p style={{whiteSpace: 'pre-wrap'}}>{bankingRow.bankAddress ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Account Currency</label>
                        <p>
                          {bankingRow.accountCurrency ?? '-'}
                          {bankingRow.accountCurrency === 'OTHER' && bankingRow.otherCurrency
                            ? ` (${bankingRow.otherCurrency})`
                            : ''}
                        </p>
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='modal_label'>Remarks</label>
                        <p style={{whiteSpace: 'pre-wrap'}}>{bankingRow.remarks ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Salary Currency</label>
                        <p>
                          {bankingRow.salaryCurrency ?? '-'}
                          {bankingRow.salaryCurrency === 'OTHER' && bankingRow.salaryOtherCurrency
                            ? ` (${bankingRow.salaryOtherCurrency})`
                            : ''}
                        </p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Basic</label>
                        <p>{formatAmount(bankingRow.basicWages ?? bankingRow.totalBasic)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Fixed Overtime (109 Hrs)</label>
                        <p>
                          {formatAmount(bankingRow.fixedOvertime109Hrs ?? bankingRow.overtimePay)}
                        </p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Leave Wages</label>
                        <p>
                          {formatAmount(bankingRow.leaveWages9DaysPerMonth ?? bankingRow.leavePay)}
                        </p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Sub / SMB</label>
                        <p>{formatAmount(bankingRow.subSmb)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Trade Allowance</label>
                        <p>{formatAmount(bankingRow.tradeAllowance)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Other Allowance</label>
                        <p>{formatAmount(bankingRow.otherAllowance ?? bankingRow.incentive)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Owner&apos;s / Loyalty Bonus</label>
                        <p>{formatAmount(bankingRow.ownersLoyaltyBonus)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Re-joining Bonus</label>
                        <p>{formatAmount(bankingRow.rejoiningBonus)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Overtime Rate</label>
                        <p>{formatAmount(bankingRow.overtimeRate)}</p>
                      </div>
                      <div className='col-md-6 mb-3'>
                        <label className='modal_label'>Total Monthly Wages</label>
                        <p>{formatAmount(bankingRow.salary)}</p>
                      </div>
                      {(() => {
                        const breakdownTotal =
                          Number(bankingRow.basicWages ?? bankingRow.totalBasic ?? 0) +
                          Number(bankingRow.leaveWages9DaysPerMonth ?? bankingRow.leavePay ?? 0) +
                          Number(bankingRow.otherAllowance ?? bankingRow.incentive ?? 0)

                        const hasAnyBreakdown = [
                          bankingRow.basicWages ?? bankingRow.totalBasic,
                          bankingRow.leaveWages9DaysPerMonth ?? bankingRow.leavePay,
                          bankingRow.otherAllowance ?? bankingRow.incentive,
                        ].some((value) => value !== null && value !== undefined)

                        const salary =
                          bankingRow.salary === null || bankingRow.salary === undefined
                            ? null
                            : Number(bankingRow.salary)

                        if (!hasAnyBreakdown) return null

                        const diff = salary === null ? null : salary - breakdownTotal
                        const hasAdjustment = diff !== null && Math.abs(diff) > 0.009

                        return (
                          <div className='col-12 mb-3'>
                            <small className='text-muted d-block'>
                              Calculated from breakdown: {formatAmount(breakdownTotal)}
                            </small>
                            {hasAdjustment && (
                              <small className='text-warning d-block mt-1'>
                                {diff! > 0
                                  ? `Extra added in total monthly wages: ${formatAmount(diff!)}`
                                  : `Manual reduction from breakdown total: ${formatAmount(
                                      Math.abs(diff!)
                                    )}`}
                              </small>
                            )}
                          </div>
                        )
                      })()}

                      <div className='col-12 mb-2 mt-2'>
                        <label className='modal_label fw-semibold'>DEDUCTIONS</label>
                      </div>
                      <div className='col-md-3 mb-3'>
                        <label className='modal_label'>Union / Social dues</label>
                        <p>As per CBA</p>
                      </div>
                      <div className='col-md-3 mb-3'>
                        <label className='modal_label'>Cash Advance</label>
                        <p>{formatAmount(bankingRow.cashAdvanceFromOffice)}</p>
                      </div>
                      <div className='col-md-3 mb-3'>
                        <label className='modal_label'>Other Deductions</label>
                        <p>{formatAmount(bankingRow.otherDeductions)}</p>
                      </div>
                      <div className='col-md-3 mb-3'>
                        <label className='modal_label'>Total</label>
                        <p>
                          {formatAmount(
                            Number(bankingRow.cashAdvanceFromOffice ?? 0) +
                              Number(bankingRow.otherDeductions ?? 0)
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ------------------------------------------------------------------ */}
                  {/* TAB 6 — Courses & Certificates                                     */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>Courses & Certificates</h5>
                  {COURSE_CATEGORIES.map((cat) => (
                    <div key={cat.label} className='mb-4'>
                      <h6 className='fw-bold mb-2'>{cat.label}</h6>
                      <div className='table-responsive'>
                        <table className='table table-bordered'>
                          <thead className='table-light'>
                            <tr>
                              <th>Course Name</th>
                              <th>Certificate Number</th>
                              <th>Date of Issue</th>
                              <th>Date of Expiry</th>
                              <th>Issued By</th>
                              <th>File</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cat.courses.map((courseName) => {
                              const row =
                                courseMap.get(`${cat.label}__${courseName}`) ||
                                courseMap.get(`__${courseName}`)
                              return (
                                <tr key={courseName}>
                                  <td>{courseName}</td>
                                  <td>{row?.certificateNumber || '-'}</td>
                                  <td>{formatDate(row?.dateOfIssue)}</td>
                                  <td>{formatDate(row?.dateOfExpiry)}</td>
                                  <td>{row?.issuedBy || '-'}</td>
                                  <td>
                                    {row?.filePath && canViewCrewDocumentFile ? (
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-info'
                                        onClick={() => openCoursePreview(row)}
                                      >
                                        View
                                      </button>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                            {(courseRows || [])
                              .filter((row) => {
                                const rowName = (row.certificateName || '').trim()
                                if (!rowName) return false

                                const rowCategory =
                                  (row.category || 'Revalidation Course').trim() ||
                                  'Revalidation Course'
                                return (
                                  rowCategory === cat.label &&
                                  !cat.courses.some((courseName) => courseName === rowName)
                                )
                              })
                              .map((row, idx) => (
                                <tr
                                  key={`custom-${cat.label}-${row.id ?? idx}-${
                                    row.certificateName || 'custom'
                                  }`}
                                >
                                  <td>{row.certificateName || '-'}</td>
                                  <td>{row.certificateNumber || '-'}</td>
                                  <td>{formatDate(row.dateOfIssue)}</td>
                                  <td>{formatDate(row.dateOfExpiry)}</td>
                                  <td>{row.issuedBy || '-'}</td>
                                  <td>
                                    {row.filePath && canViewCrewDocumentFile ? (
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-info'
                                        onClick={() => openCoursePreview(row)}
                                      >
                                        View
                                      </button>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}

                  {/* ------------------------------------------------------------------ */}
                  {/* TAB 7 — Previous Sea Service                                       */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>
                    Previous Sea Service
                    <small className='fw-normal d-block mt-1'>
                      (All sea service details from Cadets/Jr Level. List recent vessel first)
                    </small>
                  </h5>
                  <div className='table-responsive'>
                    <table
                      className='table table-bordered align-middle text-center'
                      style={{minWidth: '1200px'}}
                    >
                      <thead className='table-light'>
                        <tr>
                          <th>Sr. No.</th>
                          <th>Company Name</th>
                          <th>Vessel Name</th>
                          <th>Type of Vessel/ Flag</th>
                          <th>GRT/DWT</th>
                          <th>Engine Type</th>
                          <th>KWT/ ME BHP</th>
                          <th>Rank</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Total Months/Days</th>
                          <th>Reason for S/OFF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seaSorted.length === 0 ? (
                          <tr>
                            <td colSpan={12} className='text-center text-muted'>
                              No sea service records.
                            </td>
                          </tr>
                        ) : (
                          seaSorted.map((s, idx) => (
                            <tr key={s.id ?? idx}>
                              <td>{s.serialNo ?? idx + 1}</td>
                              <td>{s.companyName || '-'}</td>
                              <td>{s.vesselName || '-'}</td>
                              <td>{s.typeOfVesselFlag || '-'}</td>
                              <td>{s.grtdrt || '-'}</td>
                              <td>{s.engineType || '-'}</td>
                              <td>{s.kwtBhp || '-'}</td>
                              <td>{s.rank || '-'}</td>
                              <td>{formatDate(s.fromDate)}</td>
                              <td>{formatDate(s.toDate)}</td>
                              <td>{s.totalMonthsDays || '-'}</td>
                              <td>{s.reasonForSignOff || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ------------------------------------------------------------------ */}
                  {/* FLAG DOCUMENTS SECTION                                            */}
                  {/* ------------------------------------------------------------------ */}
                  <hr className='my-5' />
                  <h5 className='mb-3'>Flag Documents</h5>
                  <div className='table-responsive'>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th style={{width: '20%'}}>Flag / Document</th>
                          <th style={{width: '20%'}}>Document Number</th>
                          <th style={{width: '20%'}}>Issue Date</th>
                          <th style={{width: '20%'}}>Expiry Date</th>
                          <th style={{width: '20%'}}>File</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flagDocs.length === 0 && (
                          <tr>
                            <td colSpan={5} className='text-center text-muted'>
                              No flag documents.
                            </td>
                          </tr>
                        )}

                        {Array.from(
                          new Set([...FLAG_ORDER, ...flagDocs.map((d) => d.flag).filter(Boolean)])
                        ).map((flag) => (
                          <React.Fragment key={flag}>
                            <tr
                              className='table-secondary align-middle'
                              style={{borderTop: '2px solid #ccc'}}
                            >
                              <td colSpan={5} className='py-2 bg-light-warning'>
                                <span
                                  className='fw-bold text-uppercase badge badge-secondary badge-lg my-2'
                                  style={{letterSpacing: '0.05em'}}
                                >
                                  {FLAG_LABELS[flag] || flag.replace(/_/g, ' ')}
                                </span>
                              </td>
                            </tr>

                            {FLAG_DOC_TYPES.map((docType) => {
                              const doc = flagDocs.find(
                                (d) => d.flag === flag && d.docType === docType
                              )

                              if (!doc) {
                                return (
                                  <tr key={`${flag}-${docType}`}>
                                    <td>
                                      <strong>{FLAG_DOC_LABELS[docType]}</strong>
                                    </td>
                                    <td colSpan={3} className='text-muted'>
                                      No data
                                    </td>
                                    <td>-</td>
                                  </tr>
                                )
                              }

                              return (
                                <tr key={`${flag}-${docType}`}>
                                  <td>
                                    <strong>{FLAG_DOC_LABELS[docType]}</strong>
                                  </td>
                                  <td>{doc.documentNumber || '-'}</td>
                                  <td>{formatDate(doc.issueDate)}</td>
                                  <td>{formatDate(doc.expiryDate)}</td>
                                  <td>
                                    {doc.filePath && canViewCrewDocumentFile ? (
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-info'
                                        onClick={() =>
                                          openFlagPreview(
                                            doc,
                                            `${FLAG_LABELS[doc.flag] || doc.flag.replace(/_/g, ' ')} - ${
                                              FLAG_DOC_LABELS[doc.docType]
                                            }`
                                          )
                                        }
                                      >
                                        View
                                      </button>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* ------------------ OTHER DETAILS (tab 8) ------------------ */}
                  <h5 className='mb-3 mt-6'>Additional Information</h5>

                  {/* Pump Experience */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>PUMP EXPERIENCE</h6>
                    <div className='row'>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>FRAMO (in months)</label>
                        <p>{otherRow?.pumpExperienceFramoMonths ?? '-'}</p>
                      </div>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>COP (in months)</label>
                        <p>{otherRow?.pumpExperienceCopMonths ?? '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Vessel Experience */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>VESSEL EXPERIENCE</h6>
                    <table className='table table-bordered'>
                      <thead className='table-light'>
                        <tr>
                          <th>Have you been on board vessels during</th>
                          <th>Response</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Drydocking</td>
                          <td>{formatBoolean(otherRow?.drydockingExperience)}</td>
                          <td>
                            {otherRow?.drydockingExperience ? otherRow?.drydockingRank || '-' : '-'}
                          </td>
                        </tr>
                        <tr>
                          <td>New Construction</td>
                          <td>{formatBoolean(otherRow?.newConstructionExperience)}</td>
                          <td>
                            {otherRow?.newConstructionExperience
                              ? otherRow?.newConstructionRank || '-'
                              : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Other Questions */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>OTHER QUESTIONS</h6>
                    <div className='table-responsive'>
                      <table className='table table-bordered'>
                        <thead className='table-light'>
                          <tr>
                            <th>Question</th>
                            <th>Response</th>
                            <th>Details (if Yes)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {OTHER_QS.map((q, i) => (
                            <tr key={i}>
                              <td style={{maxWidth: 520}}>{q.label}</td>
                              <td>{formatBoolean(otherRow?.[q.flag])}</td>
                              <td>
                                {otherRow?.[q.flag]
                                  ? (otherRow?.[q.details] as string | null) || '-'
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Company Awareness */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>COMPANY AWARENESS</h6>
                    <div className='row'>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Referral Source</label>
                        <p>
                          {otherRow?.referralSource
                            ? REFERRAL_LABELS[otherRow.referralSource as string] ||
                              otherRow.referralSource
                            : '-'}
                        </p>
                      </div>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Details</label>
                        <p>{otherRow?.referralSource ? otherRow?.referralDetails || '-' : '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* References */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>Reference</h6>
                    {otherRow?.hasReferenceData ? (
                      parsedReferences.length ? (
                        <div className='table-responsive'>
                          <table className='table table-bordered'>
                            <thead className='table-light'>
                              <tr>
                                <th>Sr. No</th>
                                <th>Name of the company</th>
                                <th>PIC</th>
                                <th>Designation</th>
                                <th>Phone No</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parsedReferences.map((r, idx) => (
                                <tr key={idx}>
                                  <td>{r.srNo ?? idx + 1}</td>
                                  <td>{r.companyName || '-'}</td>
                                  <td>{r.pic || '-'}</td>
                                  <td>{r.designation || '-'}</td>
                                  <td>{r.phoneNo || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className='text-muted'>Enabled but no rows provided.</p>
                      )
                    ) : (
                      <p className='text-muted'>Not provided.</p>
                    )}
                  </div>

                  {/* Declaration */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>DECLARATION BY THE APPLICANT</h6>
                    <div className='row'>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>
                          Medical examination (incl. Drug/Alcohol test) is a condition of employment
                        </label>
                        <p>{formatBoolean(otherRow?.declarationMedicalExam)}</p>
                      </div>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>
                          Company medical officer’s decision is final
                        </label>
                        <p>{formatBoolean(otherRow?.declarationMedicalDecision)}</p>
                      </div>

                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>
                          No criminal/police investigations in progress
                        </label>
                        <p>{formatBoolean(otherRow?.declarationNoCriminal)}</p>
                      </div>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Travel documents valid and in order</label>
                        <p>{formatBoolean(otherRow?.declarationDocumentsValid)}</p>
                      </div>

                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Presently employed elsewhere</label>
                        <p>{formatBoolean(otherRow?.declarationNotEmployed)}</p>
                      </div>

                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>
                          Aware that company has no agents in India
                        </label>
                        <p>{formatBoolean(otherRow?.declarationNoAgents)}</p>
                      </div>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Available to report on/after</label>
                        <p>{formatDate(otherRow?.availabilityDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Applicant Details */}
                  <div className='mb-4'>
                    <h6 className='fw-bold mb-2'>APPLICANT DETAILS</h6>
                    <div className='row'>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Name of Applicant</label>
                        <p>{otherRow?.applicantName || '-'}</p>
                      </div>
                      <div className='col-md-6 mb-2'>
                        <label className='modal_label'>Date</label>
                        <p>{formatDate(otherRow?.applicantDate)}</p>
                      </div>
                    </div>
                  </div>

                  {showCrewMatrixForRank && (
                    <div className='mb-4 mt-5'>
                      <CrewMatrixProfileModal
                        inline
                        readOnly
                        title='Crew Matrix'
                        crewRecord={matrixCrewRecord}
                        seaServices={seaRows}
                        manualValues={crewMatrixValues}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </Modal.Body>
        </div>
      </div>

      {viewer.open && (
        <div className='modal fade show' style={{display: 'block'}} onClick={closeViewer}>
          <div className='modal-dialog modal-xl' onClick={(e) => e.stopPropagation()}>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title m-0'>{viewer.title}</h5>
                <button type='button' className='btn-close' onClick={closeViewer} />
              </div>
              <div className='modal-body' style={{height: '80vh'}}>
                {viewer.mime?.startsWith('image/') ? (
                  <img
                    src={viewer.url ?? ''}
                    alt=''
                    style={{maxWidth: '100%', maxHeight: '100%'}}
                  />
                ) : (
                  <iframe
                    src={viewer.url ?? ''}
                    width='100%'
                    height='100%'
                    title='Document preview'
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ViewCrewModal
